import Flex from '@Components/Bases/Flex';
import { useGetInfoCategoryMasterData } from '@Domains/MasterData';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  Typography,
} from '@mui/material';
import { IRelateMasterDataFormValues } from '@Router/GoalAndOperationalPlans/Containers/GoalAndOperationalPlansFormProvider/types';
import { INFO_CATEGORY_MASTER_DATA } from 'src/Enum/MasterData';
import FormCheckbox from '@Components/Form/FormCheckBox';
import { FormProvider, useForm } from 'react-hook-form';
import { useGoalAndOperationalPlansForm } from '@Router/GoalAndOperationalPlans/Containers/GoalAndOperationalPlansFormProvider';
import {
  checkValidData,
  ExtendedRelateMasterDataFormValues,
  mapGoalTopics,
  mapInitialValue,
  mapRelateDataValues,
  RelateTargetFormValues,
} from './utils';
import { FormInput } from '@Components/Form';
import { useEffect, useMemo } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { RelatedTargetModalValidationSchema } from './validators';

type RelatedTargetTopicsModalProps = {
  opened: boolean;
  close: () => void;
  onUpdateGoals: (data: IRelateMasterDataFormValues[]) => void;
  planIndex: number;
};

const RelatedTargetModal = (props: RelatedTargetTopicsModalProps) => {
  const { opened, close, onUpdateGoals, planIndex } = props;
  const [{ data: goals, loading }] = useGetInfoCategoryMasterData([
    INFO_CATEGORY_MASTER_DATA.GOAL_BUSINESS_PLAN,
    INFO_CATEGORY_MASTER_DATA.GOAL_COST_PLAN,
    INFO_CATEGORY_MASTER_DATA.GOAL_INVESTMENT_PLAN,
    INFO_CATEGORY_MASTER_DATA.GOAL_DEBT_PLAN,
  ]);

  const goalTopics = useMemo(() => mapGoalTopics(goals), [goals]);
  const otherLabel = 'อื่น ๆ';
  let currentGoalTopics = '';

  const { getValues: getValuesGoalForm } = useGoalAndOperationalPlansForm();
  const relateGoalList = getValuesGoalForm(
    `companyValueIncreasePlans.valueUpPlans.${planIndex}.relateGoals`
  );

  const formContext = useForm<RelateTargetFormValues>({
    mode: 'onChange',
    resolver: zodResolver(RelatedTargetModalValidationSchema),
    defaultValues: {},
  });
  const {
    setValue: setValueFormContext,
    handleSubmit,
    watch,
    clearErrors,
  } = formContext;

  const checkOtherDisabled = (index: number): boolean => {
    return !!watch(`relateGoals.${index}.isChecked`);
  };

  const clearName = (index: number) => {
    setValueFormContext(`relateGoals.${index}.nameTh`, '');
    setValueFormContext(`relateGoals.${index}.nameEn`, '');
    clearErrors([`relateGoals.${index}.nameTh`, `relateGoals.${index}.nameEn`]);
  };

  const handleSubmitRelateTargets = (data: RelateTargetFormValues) => {
    const result = mapRelateDataValues(data.relateGoals);
    onUpdateGoals(result);
    close();
  };

  const currentRelateGoals: ExtendedRelateMasterDataFormValues[] =
    watch(`relateGoals`) || [];
  const validData = checkValidData(currentRelateGoals);

  useEffect(() => {
    if (goalTopics && relateGoalList) {
      formContext.reset(mapInitialValue(relateGoalList, goalTopics));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [goalTopics]);

  if (loading) {
    return <CircularProgress />;
  }

  return (
    <FormProvider {...formContext}>
      <form noValidate style={{ width: '100%' }}>
        <Dialog open={opened} onClose={close} maxWidth="xl">
          <Flex p={2} gap={2} borderBottom="1px solid rgba(0, 0, 0, 0.12)">
            <Flex flexDirection="column" gap={1}>
              <Typography fontWeight="bold" fontSize={16}>
                หัวข้อเป้าหมายและแผนการทางธุรกิจ
              </Typography>
              <Flex flexDirection="column" p={1}>
                {goalTopics?.map((goalTopic, index) => {
                  const isNewCategory =
                    currentGoalTopics !== goalTopic.infoCategory;

                  if (isNewCategory) {
                    currentGoalTopics = goalTopic.infoCategory;
                  }
                  return (
                    <Box key={goalTopic.infoCategoryMasterDataId}>
                      {isNewCategory && (
                        <Typography fontWeight={800} fontSize={16} my={1}>
                          {goalTopic.infoCategory}
                        </Typography>
                      )}
                      <FormCheckbox
                        name={`relateGoals.${index}.isChecked`}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          const checked = e.target.checked;
                          setValueFormContext(
                            `relateGoals.${index}.infoCategoryMasterDataId`,
                            goalTopic.infoCategoryMasterDataId
                          );
                          if (!checked && goalTopic.nameTh === otherLabel) {
                            clearName(index);
                          }
                        }}
                        label={goalTopic.nameTh}
                      />
                      {goalTopic.nameTh === otherLabel && (
                        <Flex flexDirection={'column'}>
                          <FormInput
                            name={`relateGoals.${index}.nameTh`}
                            placeholder="กรุณาระบุเป้าหมายและแผนการขยายธุรกิจ (ไทย)"
                            sx={{ marginBottom: 1 }}
                            disabled={!checkOtherDisabled(index)}
                            required
                          />
                          <FormInput
                            name={`relateGoals.${index}.nameEn`}
                            placeholder="กรุณาระบุเป้าหมายและแผนการขยายธุรกิจ (อังกฤษ)"
                            sx={{ marginBottom: 1 }}
                            disabled={!checkOtherDisabled(index)}
                            required
                          />
                        </Flex>
                      )}
                    </Box>
                  );
                })}
              </Flex>
            </Flex>
          </Flex>
          <Flex gap={2} p={1} justifyContent="right">
            <Button variant="contained" color="inherit" onClick={close}>
              ยกเลิก
            </Button>
            <Button
              variant="contained"
              color="primary"
              disabled={!validData}
              onClick={handleSubmit(handleSubmitRelateTargets)}
            >
              ยืนยัน
            </Button>
          </Flex>
        </Dialog>
      </form>
    </FormProvider>
  );
};

export default RelatedTargetModal;
